import {
  showCustomToast,
  updateCustomToast,
} from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import { BlockBlobClient } from '@azure/storage-blob';
import { UploadFiles } from 'common/components/UploadFiles';
import { DocumentTypeOption } from 'common/components/UploadFiles/view';
import React from 'react';

import { Trip_trip } from '../../../../__generated__/Trip';
import { TripAvailableDocumentTypes } from '../__generated__/TripAvailableDocumentTypes';
import {
  TripUploadDocument,
  TripUploadDocumentVariables,
  TripUploadDocument_tripUploadDocument,
} from './__generated__/TripUploadDocument';
import { useFormContext } from 'react-hook-form';
import { TripCreateFormValues } from '../../../../types';
import TRIP_UPLOAD_DOCUMENT from './TripUploadDocument.graphql';

interface UploadFormProps {
  availableDocumentTypes: TripAvailableDocumentTypes | undefined;
  tripData?: Trip_trip | null | undefined;
  onUpload?: (
    fileSelected: File,
    documentData: TripUploadDocument_tripUploadDocument,
    toastId: string
  ) => void;
}

export const UploadForm: React.FC<UploadFormProps> = ({
  availableDocumentTypes,
  tripData,
  onUpload,
}) => {
  const { id } = { ...tripData };

  const {
    formState: { isDirty },
  } = useFormContext<TripCreateFormValues>();
  const [uploadDocument] = useMutation<
    TripUploadDocument,
    TripUploadDocumentVariables
  >(TRIP_UPLOAD_DOCUMENT, { errorPolicy: 'all' });

  const documentTypeOptions =
    availableDocumentTypes?.tripAvailableDocumentTypes?.nodes.map(
      (doctype) =>
        ({
          key: doctype.id,
          text: doctype.documentType || '',
          isAccountingDocument: doctype.isAccountingDocument,
          title: undefined,
          disabled: undefined,
        } as DocumentTypeOption)
    ) || [];

  return (
    <UploadFiles
      documentTypes={documentTypeOptions!}
      uploadDocument={{
        uploadDocumentData: async (documentType, data, filSelected) => {
          filSelected.map(async (fileEntity, fileIndex) => {
            // const toastId = `file.name.${fileEntity?.name}.${fileIndex}`;
            const toastId = showCustomToast({
              content: `Uploading ${fileEntity?.name}...`,
              appearance: 'info',
              autoDismiss: false,
            });
            const uploadMutationResults = await uploadDocument({
              variables: {
                document: {
                  ...data,
                  documentTypeId: parseInt(documentType.key.toString()),
                  filename: fileEntity.name,
                },
                tripId: id!,
              },
            });
            if (uploadMutationResults.errors) {
              updateCustomToast(toastId!, {
                content: `Upload of ${fileEntity.name} failed`,
                appearance: 'error',
                autoDismiss: true,
              });
            }
            if (
              uploadMutationResults.data?.tripUploadDocument?.document &&
              uploadMutationResults.data?.tripUploadDocument?.document
                ._documentFileId
            ) {
              onUpload?.(
                fileEntity,
                uploadMutationResults.data?.tripUploadDocument,
                toastId!
              );
              const client = new BlockBlobClient(
                uploadMutationResults.data?.tripUploadDocument?.uploadLink
              );
              await client.uploadData(fileEntity);
            }
          });
        },
      }}
      disableUpload={isDirty}
    />
  );
};
